// The pieces Konfiguration (and next Compliance) are built from, the same design language as Gennemgang:
//   TileMenu       the horizontal menu: tiles with an icon, a name and a status line, in a white frame
//   SectionHeading a coloured pill with its count and a short explanation under it; folds everything below it
//   StatusCard     one card of rows under a status (Skal rettes, Bør ses på, I orden ...), 4 rows and "Vis alle"
//   OnePagePanel   the side panel: header with a status dot, the coloured note, one expand button, folded cards, the decision pinned at the bottom
// Styles: StatusCards.css (cards and panel reuse .wqc* and .pn* from pages/WorkQueueCards.css).
import React, { useState } from 'react';
import { usePanelOpen } from '../hooks/usePanelOpen';
import '../pages/WorkQueueCards.css';
import './StatusCards.css';
import ExpandAll from './ExpandAll';
import DecisionTag from './DecisionTag';
import { getStatusBadge } from '../utils/statusBadge';
import './ItListCard.css';

const CHEVRON = (
  <svg className="wqc__cv" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);
const CLIPBOARD = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 3h6v4H9z" /><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M9 12h6M9 16h6" /></svg>
);

// tiles: [{ key, name, icon, bg, fg, status: { text, tone }, active, off, onClick }]; tone is bad, warn, ok or mute.
export function TileMenu({ tiles, label, className = '' }) {
  return (
    <div className={`tm${className ? ` ${className}` : ''}`} role="tablist" aria-label={label}>
      {tiles.map((t) => (
        <button key={t.key} type="button" role="tab" title={`${t.name}: ${t.status.text}`} aria-selected={t.active} className={`tm__t${t.active ? ' is-on' : ''}${t.off ? ' is-off' : ''}`} onClick={t.onClick}>
          <span className="tm__ic" style={{ background: t.bg, color: t.fg }}>{t.icon}</span>
          <span className="tm__x">
            <b>{t.name}</b>
            <small className={`tm__s tm__s--${t.status.tone}`}>{t.status.text}</small>
          </span>
        </button>
      ))}
    </div>
  );
}

// color: { fg } the section's own colour (the pill is solid in it). The explanation stands quiet under the pill and is
// at full strength when the mouse is over it.
export function SectionHeading({ title, extra, count, text, color, folded, onToggle, children }) {
  return (
    <div className="sx" style={{ '--sc-fg': color }}>
      <button type="button" className={`sx__h${folded ? ' is-coll' : ''}`} aria-expanded={!folded} onClick={onToggle}>
        <b>{title}</b>
        {extra && <em className="sx__x">{extra}</em>}
        <span className="sx__n">{count}</span>
        <svg className="sx__cv" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
        {text && !folded && <small>{text}</small>}
      </button>
      {!folded && <div className="sx__b">{children}</div>}
    </div>
  );
}

// One card. tone: bad, warn, ok or other. rows is the markup of the visible rows; the footer shows "Vis alle N →"
// when some are hidden, and "Træf beslutning" on every card.
export function StatusCard({ tone, title, pill, selected, folded, onFold, onActivate, rows, total, hidden, onShowAll, decideLabel, onDecide, empty, markAll, it }) {
  return (
    <section className={`wqc wqc--solo wqc--${tone}${selected ? ' is-sel' : ''}${folded ? ' is-coll' : ''}`} onClick={onActivate}>
      <header className="wqc__h" onClick={(e) => { e.stopPropagation(); onActivate(); onFold(); }}>
        <h3>{title}</h3>
        <span className={`wqc__pill wqc__pill--${pill.tone}`}>{pill.text}</span>
        <button type="button" className="wqc__chev" aria-label={folded ? 'Åbn' : 'Luk'} aria-expanded={!folded} onClick={(e) => { e.stopPropagation(); onFold(); }}>{CHEVRON}</button>
      </header>
      {!folded && (
        <>
          <div className="wqc__rows">{rows}{empty && <p className="sx__none">{empty}</p>}</div>
          <footer className="wqc__f">
            <span className="wqc__lft">
              {markAll && (
                <button type="button" className="wqc__ma" data-tip={markAll.label} aria-label={markAll.label} onClick={(e) => { e.stopPropagation(); markAll.onClick(); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 12l3 3 5-6" /></svg>
                </button>
              )}
              {hidden > 0 && <button type="button" className="wqc__more" onClick={(e) => { e.stopPropagation(); onShowAll(); }}>Vis alle {total} →</button>}
            </span>
            {selected && (
              <span className="wqc__acts">
                {it && (
                  <button type="button" className={`itl-btn${it.on ? ' is-on' : ''}`} aria-pressed={it.on} disabled={it.disabled} title={it.on ? 'På IT-opgaver. Klik for at fjerne' : 'Tilføj til IT-opgaver'} onClick={(e) => { e.stopPropagation(); it.onToggle(); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{it.on ? <path d="M5 12l5 5L20 7" /> : <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />}</svg>
                    {it.label}
                  </button>
                )}
                {onDecide && <button type="button" className="wqc__btn" onClick={(e) => { e.stopPropagation(); onDecide(); }}>{CLIPBOARD}{decideLabel}</button>}
              </span>
            )}
          </footer>
        </>
      )}
    </section>
  );
}

// One folded card of the side panel: a grey title strip with a count, and either a one-line summary or the whole
// content once the strip is pressed.
// A folded group of the side panel: the heading and the count, nothing else. (One component for every panel.)
export function PanelCard({ title, count, open, onToggle, tone, children }) {
  return (
    <section className={`pn-card${open ? ' is-open' : ''}${tone ? ` pn-card--${tone}` : ''}`}>
      <button type="button" className="pn-card__h" aria-expanded={open} onClick={onToggle}>
        {/* "Hvad er det" is one of the boxes at the top: its icon is in front of the heading, open or folded. */}
        {tone === 'what' && <span className="pn-card__i">{ICON_INFO}</span>}
        <span>{title}</span>
        {count !== null && count !== undefined && <b>{count}</b>}
        <svg className="pn-card__ch" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && <div className="pn-card__body">{children}</div>}
    </section>
  );
}

// The box at the top of every side panel, and the Anbefaling under it: one template for Gennemgang, Compliance and Konfiguration.
// tone: bad (red), warn (orange) or ok (green); one tone per panel, the same that colours the dot by the title.
const ICON_BANG = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.6M12 16.4v.01" /></svg>;
const ICON_CHECK = <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M8 12.5l3 3 5-6" /></svg>;
const ICON_ARROW_RING = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M8 12h8M13 9l3 3-3 3" /></svg>;
const ICON_INFO = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.5v.01" /></svg>;
export const TONE_OF_DOT = { r: 'bad', o: 'warn', g: 'ok' };
// The boxes at the top of a side panel (this one, Anbefaling and "Hvad er det") are one design: a small heading in capitals with its icon in
// front of it, and the text under it across the whole width. The heading says the level, as in Gennemgang.
const ALERT_LABEL = { bad: 'Kræver handling', warn: 'Bør gennemgås', ok: 'I orden' };
export function PanelAlert({ tone = 'bad', label, small, children }) {
  return (
    <div className={`pn-adv${tone === 'warn' ? ' pn-adv--warn' : tone === 'ok' ? ' pn-adv--ok' : ''}`}>
      <div className="pn-adv__h"><span className="pn-adv__i">{tone === 'ok' ? ICON_CHECK : ICON_BANG}</span><span className="pn-adv__lb">{label || ALERT_LABEL[tone]}</span></div>
      <div className="pn-adv__t"><span>{children}</span>{small && <small>{small}</small>}</div>
    </div>
  );
}
// A quiet grey note under the box and the Anbefaling: something worth knowing that is not a fault (the same grey as "Hvad er det").
export function PanelNote({ children }) {
  return (
    <div className="pn-note">
      <span className="pn-note__i"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.5v.01" /></svg></span>
      <p>{children}</p>
    </div>
  );
}
export function PanelRec({ title = 'Anbefaling', children }) {
  return (
    <div className="pn-rec">
      <div className="pn-rec__h"><span className="pn-rec__i">{ICON_ARROW_RING}</span><span className="pn-rec__lb">{title}</span></div>
      <p className="pn-rec__p">{children}</p>
    </div>
  );
}

// A list of named things inside a card (permissions, members, policies): the name, a small tag, and, when there is one, a
// short explanation behind a chevron. Long lists show the first rows and "Vis alle".
export function PanelItems({ items, limit = 25 }) {
  const { isOpen, toggle } = usePanelOpen();
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, limit);
  return (
    <div className="pi">
      {shown.map((it, i) => {
        const id = `row:${it.name}`;
        const can = !!it.desc;
        const go = typeof it.onClick === 'function';
        const o = can && isOpen(id, false);
        return (
          <div className={`pi__r${o ? ' is-open' : ''}`} key={`${it.name}-${i}`}>
            <button type="button" className="pi__h" disabled={!can && !go} aria-expanded={can ? o : undefined} onClick={go ? it.onClick : () => toggle(id, o)}>
              <b className={it.mono ? 'pi__m' : undefined}>{it.name}</b>
              {it.tag && <span>{it.tag}</span>}
              {go
                ? <svg className="pi__go" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                : can ? <svg className="pi__cv" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg> : <i className="pi__nc" />}
            </button>
            {can && o && <p>{it.desc}</p>}
          </div>
        );
      })}
      {!all && items.length > limit && <button type="button" className="pi__more" onClick={() => setAll(true)}>Vis alle {items.length} →</button>}
    </div>
  );
}

// tiles: [[label, value, tone]] tone r, o, g or n. warn: { text, cons } or null (the red Advarsel box). note: { tone: bad|warn|ok, title, text } or null.
// cards: [{ key, title, count, summary, body, open, tone, pre }] (open: starts unfolded; tone: bad, warn or ok tints the card; pre: stands under the Anbefaling, above Detaljer, and is not part of "Udvid alle"). decision: { tag, tagKey, meta, label, onOpen }.
export function OnePagePanel({ title, sub, banner, tiles, warn, note, cards, decision }) {
  // What the user has opened or closed is remembered by the card's title (also across objects and pages); a card that has not been touched
  // opens as it is built to: "Hvad er det" and the card with the problem.
  const { isOpen, toggle, setAll } = usePanelOpen();
  const idOf = (c) => `card:${c.title}`;
  const openOf = (c) => isOpen(idOf(c), c.pre ? true : !!c.open);
  const preCards = cards.filter((c) => c.pre);
  const detailCards = cards.filter((c) => !c.pre);
  const allOpen = detailCards.length > 0 && detailCards.every(openOf);
  return (
    <div className="pn">
      {banner}
      <div className="pn-h pn-h--dot">
        <div className="pn-h__t">
          <h3 title={title}>{title}</h3>
          {sub && <p>{sub}</p>}
        </div>
        {tiles[0] && <span className={`pn-dot pn-dot--${tiles[0][2]}`} title={tiles[0][1]} aria-label={tiles[0][1]} />}
      </div>
      <div className="pn-body">
        {warn && <PanelAlert tone={TONE_OF_DOT[tiles[0]?.[2]] || 'bad'} small={warn.cons}>{warn.text}</PanelAlert>}
        {note && /^Anbefaling/.test(note.title) && <PanelRec title={note.title}>{note.text}</PanelRec>}
        {/* Any other note is one of the same boxes (its heading is the title; "Status" on something that is in order reads "I orden"). */}
        {note && !/^Anbefaling/.test(note.title) && <PanelAlert tone={note.tone === 'warn' ? 'warn' : note.tone === 'ok' ? 'ok' : 'bad'} label={note.title === 'Status' && note.tone === 'ok' ? 'I orden' : note.title}>{note.text}</PanelAlert>}
        {preCards.map((c) => (
          <PanelCard key={c.key} title={c.title} count={c.count} summary={c.summary} open={openOf(c)} onToggle={() => toggle(idOf(c), openOf(c))} tone={c.tone || 'what'}>{c.body}</PanelCard>
        ))}
        {detailCards.length > 0 && (
          <div className={`pn-dh${preCards.length ? ' pn-dh--after' : ''}`}>
            <span>Detaljer</span>
            {detailCards.length > 1 && <ExpandAll allOpen={allOpen} onClick={() => setAll(detailCards.map(idOf), !allOpen)} />}
          </div>
        )}
        {detailCards.map((c) => (
          <PanelCard key={c.key} title={c.title} count={c.count} summary={c.summary} open={openOf(c)} onToggle={() => toggle(idOf(c), openOf(c))} tone={c.tone}>{c.body}</PanelCard>
        ))}
      </div>
      {/* The decision is made on the card; the panel only shows one that has been made. */}
      {decision.tag && (
        <div className="pn-foot">
          <div className="pn-foot__r"><span>Beslutning</span></div>
          <div className="pn-foot__s">
            <DecisionTag badge={getStatusBadge({ decisionType: decision.tagKey })} /><small>{decision.meta}</small>
          </div>
        </div>
      )}
    </div>
  );
}
